import React, { Component, PropsWithChildren } from 'react';
import ViewObserver, { ObserverOptionsType, DEFAULT_OPTIONS } from './view-observer';

interface ViewtrackerProps extends PropsWithChildren<{ onView: (visible: boolean) => void }> {
  once?: boolean;
  options?: ObserverOptionsType;
  onView: (visible: boolean) => void;
  onHide?: (visible: boolean) => void;
  onChange?: (visible: boolean) => void;
}

export class ViewTracker extends Component<ViewtrackerProps> {
  observer: ViewObserver | null = null;

  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  /**
   * Create the event listener on mount
   */
  componentDidMount(): void {
    const { options = DEFAULT_OPTIONS } = this.props;
    this.observer = ViewObserver.get(options);

    if (this.wrapperRef.current) {
      // Observe the container and track all child nodes
      this.observer.observe(this.wrapperRef.current, this.trackEvent);
    }
  }

  unobserve() {
    if (typeof this.wrapperRef.current !== 'undefined' && this.observer) {
      // Remove the observation listener
      if (this.wrapperRef.current) {
        this.observer.unobserve(this.wrapperRef.current);
      }
    }

    this.observer = null;
  }

  /**
   * Remove the observe listener on unmount
   */
  componentWillUnmount() {
    this.unobserve();
  }

  /**
   * Send the tracking event callback
   * @param  {Boolean} visible - the visibility state of the element
   */
  trackEvent = (visible: boolean) => {
    const { once = true, onChange = () => {}, onView, onHide = () => {} } = this.props;
    onChange(visible);

    if (visible) {
      onView(visible);

      // Unobserve the element after firing
      if (once) {
        this.unobserve();
      }
    } else {
      onHide(visible);
    }
  };

  render() {
    const { children } = this.props;
    // Use React.Children.only to indicate that there
    // should only be a single child component
    return <div ref={this.wrapperRef}>{React.Children.only(children)}</div>;
  }
}
